import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { View, Text, Pressable, Modal } from '../../lib/react-native-compat';
import { moduleForCode } from './qtyModules';
import { phaseStatus, phaseDailySeries, phasedDailyPlan } from './usePhaseMetrics';

// The higher-level job view behind the Work-tab picker's Schedule button: every
// phase over the whole job window as a Gantt bar, with its installed-vs-budget
// rollup and a day-by-day drill-down. Field users are on phones/iPads, so it is
// an in-place modal over the quick report instead of a jump into the full app.

const fmt = (v) => Math.round(v).toLocaleString();
const fmt1 = (v) => (Math.round(v * 10) / 10).toLocaleString();

const STATUS_STYLE = {
    ahead: { bg: '#ECFDF5', fg: '#065F46', label: 'Ahead' },
    ontrack: { bg: '#EFF6FF', fg: '#1D4ED8', label: 'On track' },
    behind: { bg: '#FEF2F2', fg: '#B91C1C', label: 'Behind' },
};

// Gantt bar palette, keyed by the same status the chip shows. A phase running
// over the report date is filled solid so it reads at a glance in a long list.
const BAR_STYLE = {
    ahead: { fill: '#D1FAE5', border: '#A7F3D0', active: '#059669', text: '#065F46' },
    ontrack: { fill: '#DBEAFE', border: '#BFDBFE', active: '#1D4ED8', text: '#1E40AF' },
    behind: { fill: '#FEE2E2', border: '#FECACA', active: '#DC2626', text: '#991B1B' },
    none: { fill: '#E2E8F0', border: '#CBD5E1', active: '#475569', text: '#475569' },
};

// Raw div for the scroller, not the compat ScrollView — the merged view scrolls
// itself to the phases running over the report date, so it needs a ref.
const ModalShell = ({ title, subtitle, onClose, children, width = 640, scrollRef, headerRight }) => (
    <Modal
        visible
        onRequestClose={onClose}
        style={{ width, maxWidth: '96vw', maxHeight: '86vh', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', columnGap: 10, padding: 14, borderBottom: '1px solid #E2E8F0' }}>
            <View style={{ flexShrink: 1, minWidth: 0 }}>
                <Text style={{ fontSize: 15, fontWeight: '700', color: '#0F172A' }}>{title}</Text>
                {subtitle ? <Text style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>{subtitle}</Text> : null}
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 8, flexShrink: 0 }}>
                {headerRight}
                <Pressable onPress={onClose} style={{ minHeight: 32, minWidth: 32, alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <Text style={{ fontSize: 16, color: '#64748B' }}>✕</Text>
                </Pressable>
            </View>
        </View>
        <div ref={scrollRef} style={{ maxHeight: '74vh', overflowY: 'auto', overflowX: 'hidden' }}>
            <View style={{ paddingLeft: 14, paddingRight: 14, paddingBottom: 14 }}>{children}</View>
        </div>
    </Modal>
);

// ── Job-wide Gantt window ───────────────────────────────────────────────────
// One timeline shared by every phase row: the span of all Burn Tool intervals
// (plus the report date, so "today" is always on screen), a little padding on
// each end, and the month boundaries for the axis.
const buildTimeline = (codes, intervals, reportDate) => {
    const scheduled = codes.filter((c) => intervals[c]?.start && intervals[c]?.end);
    if (scheduled.length === 0) return null;

    const rd = moment(reportDate);
    const first = moment.min([...scheduled.map((c) => moment(intervals[c].start)), rd.clone()]);
    const last = moment.max([...scheduled.map((c) => moment(intervals[c].end)), rd.clone()]);
    const minS = first.clone().subtract(3, 'days');
    const maxE = last.clone().add(4, 'days');
    const span = Math.max(maxE.diff(minS, 'days'), 1);
    const pct = (d) => Math.min(100, Math.max(0, (moment(d).diff(minS, 'days') / span) * 100));

    const months = [];
    for (let t = minS.clone().startOf('month'); t.isBefore(maxE); t.add(1, 'month')) {
        if (t.isAfter(minS)) months.push(t.clone());
    }

    return {
        pct,
        months,
        todayPct: pct(rd),
        rangeLabel: `${first.format('M/D')} – ${last.format('M/D/YY')}`,
    };
};

const LANE_H = 22;
const BAR_H = 15;
// Height of the pinned month axis — what the auto-scroll leaves clear.
const AXIS_H = 31;

// Month gridlines + the report-date marker, drawn behind every lane so the
// columns line up down the whole list.
const LaneGrid = ({ timeline }) => (
    <>
        {timeline.months.map((t) => (
            <View
                key={`g${t.format('YYYY-MM')}`}
                style={{ position: 'absolute', left: `${timeline.pct(t)}%`, top: 0, bottom: 0, width: 1, backgroundColor: '#EEF2F7', zIndex: 0 }}
            />
        ))}
        <View style={{ position: 'absolute', left: `${timeline.todayPct}%`, top: 0, bottom: 0, width: 2, backgroundColor: '#F59E0B', borderRadius: 1, zIndex: 1 }} />
    </>
);

// Qty ↔ Hours switch for the daily drill-down, in the modal header so one
// choice drives every expanded phase.
const ModeToggle = ({ mode, onChange }) => (
    <View style={{ flexDirection: 'row', flexShrink: 0, backgroundColor: '#F1F5F9', borderRadius: 8, padding: 2 }}>
        {[['qty', 'Qty'], ['mh', 'Hours']].map(([key, label]) => {
            const on = mode === key;
            return (
                <Pressable
                    key={key}
                    onPress={() => onChange(key)}
                    title={key === 'qty' ? 'Day-by-day installed quantities' : 'Day-by-day labor hours'}
                    style={{
                        paddingTop: 4, paddingBottom: 4, paddingLeft: 10, paddingRight: 10,
                        borderRadius: 6, cursor: 'pointer',
                        backgroundColor: on ? '#FFFFFF' : 'transparent',
                        boxShadow: on ? '0 1px 2px rgba(15, 23, 42, 0.12)' : 'none',
                    }}
                >
                    <Text style={{ fontSize: 11, fontWeight: '700', color: on ? '#0F172A' : '#64748B', whiteSpace: 'nowrap' }}>{label}</Text>
                </Pressable>
            );
        })}
    </View>
);

// ── Per-phase daily drill-down ─────────────────────────────────────────────
// Date columns across the phase's Burn Tool interval, one row each for
// baseline / estimate / actual — quantities or labor hours, per the header
// toggle — with a running total column. Scrolls horizontally under a pinned
// row-label gutter — long phases are months wide and these are read on
// phones and iPads.
const DAY_W = 46;
const LBL_W = 68;
const TOT_W = 62;

const cellText = (v) => {
    if (!v) return '';
    const r = v >= 100 ? Math.round(v) : Math.round(v * 10) / 10;
    return r >= 1000 ? Math.round(r).toLocaleString() : String(r);
};

const PhaseDailyGrid = ({ interval, reportDate, budget, actualByDay, uom, plannedRanges, plannedQty = null, mode = 'qty' }) => {
    // Hours mode keys the baseline off the phased Burn Tool crew rows, so the
    // planned ramp survives instead of an even spread of the total. Qty mode
    // uses the saved quantity schedule's per-day curve when the phase has one;
    // without it the series falls back to the even spread.
    const plannedByDay = useMemo(
        () => (mode === 'mh' ? phasedDailyPlan(plannedRanges) : (plannedQty || null)),
        [mode, plannedRanges, plannedQty],
    );
    const series = phaseDailySeries({ interval, reportDate, budgetTotal: budget, actualByDay, plannedByDay });
    // A months-long phase opens on its start date otherwise — scroll the report
    // date into the middle so the useful columns are the ones on screen. Raw
    // div, not the compat ScrollView, so the scroll position is reachable.
    const scrollRef = useRef(null);
    const days = series?.days;
    const firstDay = days?.[0];
    const dayCount = days?.length || 0;
    useEffect(() => {
        const el = scrollRef.current;
        if (!el || !dayCount) return;
        const idx = moment(reportDate).diff(moment(firstDay), 'days');
        if (idx < 0 || idx >= dayCount) return;
        el.scrollLeft = Math.max(0, (idx + 0.5) * DAY_W - el.clientWidth / 2);
    }, [firstDay, dayCount, reportDate]);

    if (!series) {
        return (
            <Text style={{ fontSize: 11, color: '#94A3B8', marginTop: 8 }}>
                No schedule or daily history for this phase yet.
            </Text>
        );
    }
    const { baselineOf, estOf, actualOf, truncated } = series;
    const dataRows = [
        { label: 'Baseline', color: '#94A3B8', valueOf: baselineOf },
        { label: 'Estimate', color: '#2563EB', valueOf: estOf },
        { label: 'Actual', color: '#0F172A', valueOf: actualOf },
    ];
    const isOff = (d) => {
        const wd = moment(d).day();
        return wd === 0 || wd === 6;
    };

    return (
        <View style={{ marginTop: 8, borderTop: '1px solid #E2E8F0', paddingTop: 8 }}>
            <View style={{ flexDirection: 'row' }}>
                {/* pinned row labels */}
                <View style={{ width: LBL_W, flexShrink: 0 }}>
                    <View style={{ height: 30 }} />
                    {dataRows.map((r) => (
                        <Text
                            key={r.label}
                            style={{ height: 20, lineHeight: '20px', fontSize: 10, fontWeight: '700', color: r.color, textTransform: 'uppercase', letterSpacing: 0.3 }}
                        >
                            {r.label}
                        </Text>
                    ))}
                </View>
                <div ref={scrollRef} style={{ flex: '1 1 0%', minWidth: 0, overflowX: 'auto', overflowY: 'hidden' }}>
                    <View style={{ flexShrink: 0, width: 'fit-content' }}>
                        {/* date header */}
                        <View style={{ flexDirection: 'row', height: 30 }}>
                            {days.map((d) => {
                                const today = d === reportDate;
                                return (
                                    <View
                                        key={d}
                                        style={{
                                            width: DAY_W,
                                            flexShrink: 0,
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            paddingBottom: 3,
                                            backgroundColor: today ? '#FEF3C7' : (isOff(d) ? 'rgba(148, 163, 184, 0.12)' : 'transparent'),
                                            borderTopLeftRadius: 4,
                                            borderTopRightRadius: 4,
                                        }}
                                    >
                                        <Text style={{ fontSize: 8, color: today ? '#B45309' : '#94A3B8', whiteSpace: 'nowrap' }}>
                                            {moment(d).format('ddd')}
                                        </Text>
                                        <Text style={{ fontSize: 10, fontWeight: today ? '700' : '600', color: today ? '#B45309' : '#475569', whiteSpace: 'nowrap' }}>
                                            {moment(d).format('M/D')}
                                        </Text>
                                    </View>
                                );
                            })}
                        </View>
                        {/* one row per measure */}
                        {dataRows.map((r) => (
                                <View key={r.label} style={{ flexDirection: 'row', height: 20 }}>
                                    {days.map((d) => (
                                        <Text
                                            key={d}
                                            style={{
                                                width: DAY_W,
                                                flexShrink: 0,
                                                fontSize: 10,
                                                lineHeight: '20px',
                                                textAlign: 'center',
                                                color: r.color,
                                                backgroundColor: d === reportDate ? '#FEF9E7' : (isOff(d) ? 'rgba(148, 163, 184, 0.12)' : 'transparent'),
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            {cellText(r.valueOf(d))}
                                        </Text>
                                    ))}
                                </View>
                        ))}
                    </View>
                </div>
                {/* pinned totals — the scroll can be weeks wide, the total is
                    what everyone reads first */}
                <View style={{ width: TOT_W, flexShrink: 0, borderLeft: '1px solid #E2E8F0' }}>
                    <View style={{ height: 30, alignItems: 'center', justifyContent: 'flex-end', paddingBottom: 3 }}>
                        <Text style={{ fontSize: 9, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.3 }}>Total</Text>
                    </View>
                    {dataRows.map((r) => (
                        <Text
                            key={r.label}
                            style={{ height: 20, lineHeight: '20px', fontSize: 10, fontWeight: '700', textAlign: 'center', color: r.color }}
                        >
                            {cellText(days.reduce((t, d) => t + r.valueOf(d), 0))}
                        </Text>
                    ))}
                </View>
            </View>
            <Text style={{ fontSize: 10, color: '#94A3B8', marginTop: 6 }}>
                {mode === 'mh'
                    ? `Labor hours (MH) per day · baseline is the phased Burn Tool crew plan, estimate is the run rate left to finish${truncated ? ' · window trimmed to the first 400 days' : ''}`
                    : `Quantities${uom ? ` in ${uom}` : ''} per day · baseline ${plannedQty ? 'is the saved quantity schedule' : 'spreads the budget across the Burn Tool dates'}, estimate is the run rate left to finish${truncated ? ' · window trimmed to the first 400 days' : ''}`}
            </Text>
        </View>
    );
};

// ── Schedule — Gantt bar, budget vs installed and hours, per phase ──────────
export const ScheduleModal = ({ onClose, intervals, descOf, reportDate, qtyHist, actualHours, fpQty, phasedHours }) => {
    // What the daily drill-down shows — installed quantities (the default) or
    // labor hours against the phased crew plan. One switch for every phase.
    const [mode, setMode] = useState('qty');
    // Phases the user has drilled into — several can stay open at once.
    const [expanded, setExpanded] = useState(() => new Set());
    const toggleExpanded = (code) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(code)) next.delete(code); else next.add(code);
        return next;
    });

    // Every phase with any signal: a budget, submitted/FP quantities, or hours.
    const codes = [...new Set([
        ...Object.keys(qtyHist.budgets || {}),
        ...Object.keys(qtyHist.toDate || {}),
        ...Object.keys(fpQty || {}),
        ...Object.keys(actualHours || {}),
    ])].sort((a, b) => {
        const sa = intervals[a]?.start || '9999';
        const sb = intervals[b]?.start || '9999';
        return sa < sb ? -1 : sa > sb ? 1 : a.localeCompare(b);
    });

    const rows = codes.map((c) => {
        const desc = descOf(c);
        const uom = moduleForCode(c, desc)?.uom || '';
        const budget = qtyHist.budgets?.[c] || 0;
        const done = (qtyHist.toDate?.[c] || 0)
            + Object.entries(fpQty?.[c] || {}).reduce((t, [d, v]) => t + (d <= reportDate ? v : 0), 0);
        const mh = Object.entries(actualHours?.[c] || {}).reduce((t, [d, v]) => t + (d <= reportDate ? v : 0), 0);
        const budgetHrs = (phasedHours?.[c] || []).reduce((t, r) =>
            t + r.daily * (moment(r.end).diff(moment(r.start), 'days') + 1), 0);
        const plan = qtyHist.planFor?.(c) || null;
        const status = phaseStatus(budget, done, intervals[c], reportDate, plan?.perDay)
            || phaseStatus(budgetHrs, mh, intervals[c], reportDate);
        // Per-day installs for the drill-down: prior submissions plus HeavyJob
        // FP postings (jobs still on HeavyJob have no report history).
        const actualByDay = {};
        (qtyHist.daily?.[c] || []).forEach((d) => { actualByDay[d.date] = (actualByDay[d.date] || 0) + d.qty; });
        Object.entries(fpQty?.[c] || {}).forEach(([d, v]) => { actualByDay[d] = (actualByDay[d] || 0) + v; });
        return { code: c, desc, uom, budget, done, mh, budgetHrs, prod: mh > 0 ? done / mh : null, status, actualByDay, plan };
    }).filter((r) => r.budget || r.done || r.mh);

    // Phases with a Burn Tool interval get a bar on one shared job timeline;
    // the rest still list, with the lane marked unscheduled.
    const timeline = buildTimeline(rows.map((r) => r.code), intervals, reportDate);

    // Open on the work that is actually running today: the list is the whole
    // job in date order, so scroll the first phase whose interval covers the
    // report date up under the axis.
    const scrollRef = useRef(null);
    const activeRowRef = useRef(null);
    const firstActive = rows.find((r) => {
        const iv = intervals[r.code];
        return iv?.start && iv?.end && iv.start <= reportDate && reportDate <= iv.end;
    })?.code || null;
    useEffect(() => {
        const sc = scrollRef.current;
        const el = activeRowRef.current;
        if (!sc || !el) return;
        const top = el.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop;
        sc.scrollTop = Math.max(0, top - AXIS_H - 6);
    }, [firstActive, reportDate]);

    return (
        <ModalShell
            title="Schedule"
            subtitle={`Job phases${timeline ? ` · ${timeline.rangeLabel}` : ''} — tap a phase for its day-by-day ${mode === 'mh' ? 'hours' : 'quantities'}`}
            onClose={onClose}
            width={760}
            scrollRef={scrollRef}
            headerRight={<ModeToggle mode={mode} onChange={setMode} />}
        >
            {/* Month axis, pinned so the bars keep their dates while scrolling */}
            {timeline && (
                <div style={{ position: 'sticky', top: 0, zIndex: 6, background: '#fff', paddingTop: 12, paddingBottom: 4, borderBottom: '1px solid #E2E8F0' }}>
                    <View style={{ position: 'relative', height: 14 }}>
                        {timeline.months.map((t) => (
                            <Text key={t.format('YYYY-MM')} style={{ position: 'absolute', left: `${timeline.pct(t)}%`, top: 0, fontSize: 10, fontWeight: '600', color: '#94A3B8', whiteSpace: 'nowrap' }}>
                                {t.format('MMM')}
                            </Text>
                        ))}
                        <Text style={{ position: 'absolute', left: `${timeline.todayPct}%`, top: 0, fontSize: 9, fontWeight: '700', color: '#B45309', backgroundColor: '#FEF3C7', borderRadius: 4, paddingLeft: 3, paddingRight: 3, transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
                            {moment(reportDate).format('M/D')}
                        </Text>
                    </View>
                </div>
            )}
            {rows.length === 0 && (
                <Text style={{ fontSize: 13, color: '#64748B', marginTop: 12 }}>No quantities or hours recorded on this job yet.</Text>
            )}
            {rows.map((r) => {
                const pctDone = r.budget > 0 ? Math.min(100, Math.round((r.done / r.budget) * 100)) : null;
                const ss = r.status && STATUS_STYLE[r.status.state];
                const open = expanded.has(r.code);
                const metrics = [
                    r.budget > 0
                        ? `${fmt(r.done)} / ${fmt(r.budget)}${r.uom ? ` ${r.uom}` : ''}${pctDone != null ? ` (${pctDone}%)` : ''}`
                        : (r.done > 0 ? `${fmt(r.done)}${r.uom ? ` ${r.uom}` : ''} installed` : null),
                    r.mh > 0 ? `${fmt(r.mh)} MH` : null,
                    r.prod != null && r.done > 0 ? `${fmt1(r.prod)}${r.uom ? ` ${r.uom}` : ''}/MH` : null,
                ].filter(Boolean).join(' · ');

                // Gantt bar for this phase's Burn Tool interval, on the shared
                // job timeline. Solid when it runs over the report date.
                const iv = intervals[r.code];
                const scheduled = !!(iv?.start && iv?.end);
                const active = scheduled && iv.start <= reportDate && reportDate <= iv.end;
                const left = scheduled ? timeline.pct(iv.start) : 0;
                const width = scheduled ? Math.max(timeline.pct(moment(iv.end).add(1, 'days')) - left, 1.5) : 0;
                const bar = BAR_STYLE[r.status?.state] || BAR_STYLE.none;
                const barLabel = scheduled ? `${moment(iv.start).format('M/D')} – ${moment(iv.end).format('M/D')}` : '';
                const labelInside = width >= 22;

                return (
                    <div
                        key={r.code}
                        ref={r.code === firstActive ? activeRowRef : null}
                        style={{ borderBottom: '1px solid #F1F5F9', background: open ? '#F8FAFC' : 'transparent' }}
                    >
                        {/* Row: caret · code + description over its metrics · status
                            and progress in a fixed right column so they line up
                            down the list. */}
                        <Pressable
                            onPress={() => toggleExpanded(r.code)}
                            title={open ? 'Hide the daily detail' : 'Show baseline / estimate / actual by day'}
                            style={{ flexDirection: 'row', alignItems: 'center', columnGap: 10, paddingTop: 7, paddingBottom: 7, paddingLeft: 4, paddingRight: 4, cursor: 'pointer' }}
                        >
                            <Text style={{ width: 12, flexShrink: 0, fontSize: 10, fontWeight: '700', color: '#94A3B8' }}>
                                {open ? '▾' : '▸'}
                            </Text>
                            <View style={{ flex: 1, minWidth: 0 }}>
                                <Text numberOfLines={1} style={{ fontSize: 13, fontWeight: '600', color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {`${r.code}${r.desc ? ` — ${r.desc}` : ''}`}
                                </Text>
                                {metrics ? (
                                    <Text numberOfLines={1} style={{ fontSize: 11, color: '#64748B', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {metrics}
                                    </Text>
                                ) : null}
                            </View>
                            <View style={{ width: 150, flexShrink: 0, alignItems: 'flex-end' }}>
                                {ss && (
                                    <View style={{ borderRadius: 999, backgroundColor: ss.bg, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8 }}>
                                        <Text style={{ fontSize: 10, fontWeight: '700', color: ss.fg, whiteSpace: 'nowrap' }}>
                                            {`${ss.label} · ${r.status.pctDone}% vs ${r.status.pctPlanned}%`}
                                        </Text>
                                    </View>
                                )}
                                {r.budget > 0 && (
                                    <View style={{ width: '100%', height: 4, borderRadius: 2, backgroundColor: '#E2E8F0', overflow: 'hidden', marginTop: 5 }}>
                                        <View style={{ width: `${pctDone}%`, height: '100%', backgroundColor: '#16A34A' }} />
                                    </View>
                                )}
                            </View>
                        </Pressable>
                        {/* Gantt lane — the phase's schedule against the job */}
                        {timeline && (
                            <Pressable
                                onPress={() => toggleExpanded(r.code)}
                                title={scheduled ? `${r.code} ${r.desc}\n${barLabel}` : 'Not on the Burn Tool schedule'}
                                style={{ position: 'relative', height: LANE_H, marginBottom: 3, cursor: 'pointer' }}
                            >
                                <LaneGrid timeline={timeline} />
                                {scheduled ? (
                                    <>
                                        <View
                                            style={{
                                                position: 'absolute', left: `${left}%`, width: `${width}%`,
                                                top: (LANE_H - BAR_H) / 2, height: BAR_H, borderRadius: 5,
                                                backgroundColor: active ? bar.active : bar.fill,
                                                border: `1px solid ${active ? bar.active : bar.border}`,
                                                overflow: 'hidden', justifyContent: 'center', paddingLeft: 5, paddingRight: 5, zIndex: 2,
                                            }}
                                        >
                                            {labelInside && (
                                                <Text numberOfLines={1} style={{ fontSize: 9.5, fontWeight: '600', color: active ? '#fff' : bar.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {barLabel}
                                                </Text>
                                            )}
                                        </View>
                                        {!labelInside && (
                                            left < 55 ? (
                                                <Text numberOfLines={1} style={{ position: 'absolute', left: `calc(${left + width}% + 5px)`, top: 5, fontSize: 9.5, color: '#94A3B8', whiteSpace: 'nowrap', maxWidth: '42%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {barLabel}
                                                </Text>
                                            ) : (
                                                <Text numberOfLines={1} style={{ position: 'absolute', right: `calc(${100 - left}% + 5px)`, top: 5, fontSize: 9.5, color: '#94A3B8', whiteSpace: 'nowrap', maxWidth: '42%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {barLabel}
                                                </Text>
                                            )
                                        )}
                                    </>
                                ) : (
                                    <Text style={{ position: 'absolute', left: 0, top: 5, fontSize: 9.5, color: '#CBD5E1', whiteSpace: 'nowrap', zIndex: 2 }}>
                                        Not on the schedule
                                    </Text>
                                )}
                            </Pressable>
                        )}
                        {open && (
                            <View style={{ paddingLeft: 22, paddingRight: 4, paddingBottom: 10 }}>
                                <PhaseDailyGrid
                                    interval={intervals[r.code]}
                                    reportDate={reportDate}
                                    budget={mode === 'mh' ? r.budgetHrs : r.budget}
                                    actualByDay={mode === 'mh' ? (actualHours?.[r.code] || {}) : r.actualByDay}
                                    plannedRanges={mode === 'mh' ? (phasedHours?.[r.code] || null) : null}
                                    plannedQty={mode === 'qty' ? (r.plan?.perDay || null) : null}
                                    uom={mode === 'mh' ? 'MH' : r.uom}
                                    mode={mode}
                                />
                            </View>
                        )}
                    </div>
                );
            })}
        </ModalShell>
    );
};
